import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { Loader2 } from 'lucide-react'
import axios from 'axios'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Button } from 'components/ui/button'
import { Alert } from 'components/ui/alert'
import routes from 'constants/routes'
import VerificationCodePopupCN from './VerificationCodePopupCN'

const validationSchema = Yup.object().shape({
	name: Yup.string().required('Full name is required'),
	email: Yup.string().email('Invalid email address').required('Email is required'),
})

const CreateAccountCN = () => {
	const [errorMessage, setErrorMessage] = useState('')
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [showVerificationPopup, setShowVerificationPopup] = useState(false)
	const [userData, setUserData] = useState<{ name: string; email: string } | null>(null)
	const navigate = useNavigate()
	// An invitation email links here as /register?email=…&name=… (MYD-547).
	// Signing up with the invited address is what connects the new account to
	// the person who invited them, so it starts filled in.
	const [searchParams] = useSearchParams()
	const invited = { name: searchParams.get('name') ?? '', email: searchParams.get('email') ?? '' }

	const handleCreateAccount = async (values: { name: string; email: string }) => {
		setErrorMessage('')
		setIsSubmitting(true)

		try {
			const firstName = values.name.split(' ')[0] || values.name

			const response = await axios.post('/api/users/send-verification-code', {
				name: values.name,
				firstName,
				email: values.email,
			})

			if (response.data.success) {
				setUserData({ name: values.name, email: values.email })
				setShowVerificationPopup(true)
			} else {
				setErrorMessage(response.data.message || 'Failed to send verification code')
			}
		} catch (error: any) {
			const message =
				error.response?.data?.message ||
				(error.message === 'Network Error'
					? 'Unable to connect to the server. Please check your internet connection.'
					: 'Failed to send verification code. Please try again.')
			setErrorMessage(message)
		} finally {
			setIsSubmitting(false)
		}
	}

	const handleVerificationSuccess = () => {
		setShowVerificationPopup(false)
		navigate(routes.HOME)
	}

	const handleVerificationCancel = () => {
		setShowVerificationPopup(false)
		setUserData(null)
	}

	return (
		<>
			<Formik
				initialValues={invited}
				validationSchema={validationSchema}
				onSubmit={handleCreateAccount}
			>
				{({ handleChange, handleBlur, values, errors, touched }) => {
					const isFormValid = values.name.trim() !== '' && values.email.trim() !== ''

					return (
						<Form className="space-y-5">
							<h2 className="text-2xl font-semibold text-center text-[#074f9b]">
								Create Account
							</h2>

							{errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

							<div className="space-y-4">
								<div className="space-y-1.5">
									<Label htmlFor="name">Full Name</Label>
									<Input
										id="name"
										name="name"
										type="text"
										placeholder="Enter your full name"
										value={values.name}
										onChange={handleChange}
										onBlur={handleBlur}
										className="h-11"
									/>
									{touched.name && errors.name && (
										<p className="text-xs text-red-500 mt-1">{errors.name}</p>
									)}
								</div>

								<div className="space-y-1.5">
									<Label htmlFor="email">Email</Label>
									<Input
										id="email"
										name="email"
										type="email"
										placeholder="Enter your email"
										value={values.email}
										onChange={handleChange}
										onBlur={handleBlur}
										className="h-11"
									/>
									{touched.email && errors.email && (
										<p className="text-xs text-red-500 mt-1">{errors.email}</p>
									)}
								</div>
							</div>

							<Button
								type="submit"
								isLarge
								disabled={!isFormValid || isSubmitting}
								className="w-full"
							>
								{isSubmitting ? (
									<>
										<Loader2 className="w-4 h-4 animate-spin" />
										Sending Code...
									</>
								) : (
									'Create Account'
								)}
							</Button>
						</Form>
					)
				}}
			</Formik>

			{showVerificationPopup && userData && (
				<VerificationCodePopupCN
					email={userData.email}
					name={userData.name}
					onSuccess={handleVerificationSuccess}
					onCancel={handleVerificationCancel}
				/>
			)}
		</>
	)
}

export default CreateAccountCN
